/**
 * Servicio: Políticas de permisos por rol (RBAC dinámico)
 *
 * Colecciones:
 *   /permissionDefinitions   — catálogo de permisos (id = "{module}.{action}")
 *   /rolePolicies            — políticas por rol (id = "{role}" o "{role}__{clubId}")
 *
 * Comportamiento:
 * - El catálogo de permisos se inicializa con `seedPermissionDefinitions`
 *   (idempotente; sólo crea los que faltan).
 * - Las políticas globales por rol se inicializan con
 *   `seedGlobalRolePolicies` a partir del catálogo (`defaultRoles`).
 * - Los overrides por club se guardan como docs con id `{role}__{clubId}`.
 * - El cálculo del set efectivo de permisos para un usuario se hace en el hook
 *   `useEffectivePermissions` (ver `hooks/useEffectivePermissions.ts`).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { PERMISSION_SEEDS, toPermissionDoc } from "@/lib/constants/permissions";
import type {
  MemberRole,
  PermissionDefinition,
  RolePermissionPolicy,
} from "@/types";

const PERMS_COLLECTION = "permissionDefinitions";
const POLICIES_COLLECTION = "rolePolicies";

/** Todos los roles de miembro que reciben políticas. `member` se excluye. */
const POLICY_ROLES: MemberRole[] = [
  "director",
  "subdirector",
  "consejero",
  "instructor",
  "tesorero",
  "secretario",
];

// ==================== IDS ====================

/** id determinístico para la política global de un rol. */
export function globalPolicyId(role: MemberRole): string {
  return role;
}

/** id determinístico para override de política a nivel club. */
export function clubPolicyId(role: MemberRole, clubId: string): string {
  return `${role}__${clubId}`;
}

// ==================== QUERIES ====================

export async function getAllPermissionDefinitions(): Promise<
  PermissionDefinition[]
> {
  const snap = await getDocs(collection(db, PERMS_COLLECTION));
  return snap.docs.map(
    (d) => ({ id: d.id, ...d.data() }) as PermissionDefinition,
  );
}

export async function getGlobalPolicy(
  role: MemberRole,
): Promise<RolePermissionPolicy | null> {
  const snap = await getDoc(doc(db, POLICIES_COLLECTION, globalPolicyId(role)));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as RolePermissionPolicy;
}

export async function getClubOverride(
  role: MemberRole,
  clubId: string,
): Promise<RolePermissionPolicy | null> {
  const snap = await getDoc(
    doc(db, POLICIES_COLLECTION, clubPolicyId(role, clubId)),
  );
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as RolePermissionPolicy;
}

/** Todas las políticas (globales + overrides) — útil para admin de RBAC. */
export async function getAllPolicies(): Promise<RolePermissionPolicy[]> {
  const snap = await getDocs(collection(db, POLICIES_COLLECTION));
  return snap.docs.map(
    (d) => ({ id: d.id, ...d.data() }) as RolePermissionPolicy,
  );
}

/** Overrides existentes para un club dado. */
export async function getClubOverridesFor(
  clubId: string,
): Promise<RolePermissionPolicy[]> {
  const q = query(
    collection(db, POLICIES_COLLECTION),
    where("scope", "==", "club"),
    where("clubId", "==", clubId),
  );
  const snap = await getDocs(q);
  return snap.docs.map(
    (d) => ({ id: d.id, ...d.data() }) as RolePermissionPolicy,
  );
}

// ==================== SEEDS ====================

/**
 * Sube al catálogo los permisos que faltan. Idempotente:
 * no pisa descripciones ni `enabled` de los ya existentes.
 */
export async function seedPermissionDefinitions(actor: string): Promise<{
  created: number;
  skipped: number;
}> {
  const existing = new Set(
    (await getAllPermissionDefinitions()).map((p) => p.id),
  );
  const batch = writeBatch(db);
  let created = 0;
  let skipped = 0;
  for (const seed of PERMISSION_SEEDS) {
    if (existing.has(seed.id)) {
      skipped++;
      continue;
    }
    const ref = doc(db, PERMS_COLLECTION, seed.id);
    batch.set(ref, {
      ...toPermissionDoc(seed),
      createdAt: serverTimestamp(),
      createdBy: actor,
      updatedAt: serverTimestamp(),
      updatedBy: actor,
    });
    created++;
  }
  if (created > 0) await batch.commit();
  return { created, skipped };
}

/**
 * Crea las políticas globales por rol si NO existen. Idempotente.
 * Se calculan a partir de `PermissionDefinition.defaultRoles`.
 */
export async function seedGlobalRolePolicies(actor: string): Promise<{
  created: number;
  skipped: number;
}> {
  const catalog = await getAllPermissionDefinitions();
  const permsByRole = new Map<MemberRole, string[]>();
  for (const role of POLICY_ROLES) {
    permsByRole.set(
      role,
      catalog
        .filter((p) => p.enabled !== false && p.defaultRoles?.includes(role))
        .map((p) => p.id),
    );
  }

  const batch = writeBatch(db);
  let created = 0;
  let skipped = 0;

  for (const role of POLICY_ROLES) {
    const ref = doc(db, POLICIES_COLLECTION, globalPolicyId(role));
    const existing = await getDoc(ref);
    if (existing.exists()) {
      skipped++;
      continue;
    }
    batch.set(ref, {
      role,
      scope: "global",
      permissions: permsByRole.get(role) ?? [],
      createdAt: serverTimestamp(),
      createdBy: actor,
      updatedAt: serverTimestamp(),
      updatedBy: actor,
    });
    created++;
  }
  if (created > 0) await batch.commit();
  return { created, skipped };
}

// ==================== COMMANDS ====================

/**
 * Reemplaza la lista de permisos de una política global. Si no existe, la
 * crea. Devuelve la política resultante.
 */
export async function setGlobalPolicyPermissions(
  role: MemberRole,
  permissions: string[],
  actor: string,
): Promise<void> {
  const ref = doc(db, POLICIES_COLLECTION, globalPolicyId(role));
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, {
      permissions,
      updatedAt: serverTimestamp(),
      updatedBy: actor,
    });
    return;
  }
  await setDoc(ref, {
    role,
    scope: "global",
    permissions,
    createdAt: serverTimestamp(),
    createdBy: actor,
    updatedAt: serverTimestamp(),
    updatedBy: actor,
  });
}

/**
 * Crea o reemplaza el override de política para un club específico.
 * Pasa `permissions = null` para BORRAR el override (vuelve a la política
 * global).
 */
export async function setClubOverride(
  role: MemberRole,
  clubId: string,
  permissions: string[] | null,
  actor: string,
  notes?: string,
): Promise<void> {
  const ref = doc(db, POLICIES_COLLECTION, clubPolicyId(role, clubId));
  if (permissions === null) {
    // Borrar: sólo si existe
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    // No usamos deleteDoc directamente para conservar un "trail" via updatedBy
    await updateDoc(ref, {
      permissions: [],
      notes: notes ?? "cleared",
      updatedAt: serverTimestamp(),
      updatedBy: actor,
    });
    return;
  }
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, {
      permissions,
      notes: notes ?? null,
      updatedAt: serverTimestamp(),
      updatedBy: actor,
    });
    return;
  }
  await setDoc(ref, {
    role,
    scope: "club",
    clubId,
    permissions,
    notes: notes ?? null,
    createdAt: serverTimestamp(),
    createdBy: actor,
    updatedAt: serverTimestamp(),
    updatedBy: actor,
  });
}

/**
 * Calcula el set de permisos aplicables a un rol dentro de un club. Prioriza
 * override club > global. Devuelve un Set para lookup O(1) en la UI.
 */
export function combinePolicies(
  global?: RolePermissionPolicy | null,
  clubOverride?: RolePermissionPolicy | null,
): Set<string> {
  if (clubOverride) return new Set(clubOverride.permissions);
  if (global) return new Set(global.permissions);
  return new Set();
}
